import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import {
    ArrowLeft,
    Calendar,
    User01,
    CheckCircle,
    XCircle,
    Clock,
    Edit01,
    Upload01,
    AlertCircle
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { TextArea } from '@/components/base/textarea/textarea';
import { Input } from '@/components/base/input/input';
import { DialogTrigger, Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { orderService } from '@/data/mock-orders';
import { accountService } from '@/data/mock-accounts';
import { activityService } from '@/data/mock-activities';
import { shipperQuoteService } from '@/data/mock-quotes';
import { addressBookService } from '@/data/mock-address-book';
import { getWeekRange } from '@/utils/week-utils';
import type { Order } from '@/types/order';
import { useTranslation } from 'react-i18next';
import { storage } from '@/lib/local-storage';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

export const ShipperOrderDetailPage = () => {
    const { t } = useTranslation(['pages:orders', 'common', 'types']);
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState<Order | null>(null);
    const [receiver, setReceiver] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [actionMode, setActionMode] = useState<'accept' | 'reject' | 'modify' | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [proposedChanges, setProposedChanges] = useState('');
    const [packingListProvided, setPackingListProvided] = useState('');
    const [contractualInfo, setContractualInfo] = useState({
        contractNumber: '',
        contractDate: '',
        terms: '',
        notes: ''
    });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isCreatingQuotes, setIsCreatingQuotes] = useState(false);

    const getStatusColor = (status: Order['status']) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Accepted': return 'success';
            case 'Rejected': return 'error';
            case 'Modified': return 'warning';
            case 'Confirmed': return 'success';
            case 'Cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: Order['status']) => {
        return t(`status.${status.toLowerCase()}`, { ns: 'types' });
    };

    useEffect(() => {
        const loadData = async () => {
            if (!id) return;

            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Load order
            const orderData = orderService.getById(id);
            if (!orderData) {
                setIsLoading(false);
                return;
            }
            setOrder(orderData);

            // Load receiver info
            const receiverData = accountService.getById(orderData.receiverId);
            setReceiver(receiverData);

            // Initialize form data
            if (orderData.contractualInfo) {
                setContractualInfo({
                    contractNumber: orderData.contractualInfo.contractNumber || '',
                    contractDate: orderData.contractualInfo.contractDate?.toISOString().split('T')[0] || '',
                    terms: orderData.contractualInfo.terms || '',
                    notes: orderData.contractualInfo.notes || ''
                });
            }
            if (orderData.packingList.provided) {
                setPackingListProvided(orderData.packingList.provided);
            }

            // Check for action mode from URL
            const action = searchParams.get('action');
            if (action === 'reject' || action === 'modify') {
                setActionMode(action);
            }

            setIsLoading(false);
        };
        loadData();
    }, [id, searchParams]);

    const handleAccept = async () => {
        if (!order) return;
        setShowConfirmDialog(true);
    };

    const handleAcceptWithoutQuotes = async () => {
        if (!order) return;

        orderService.update(order.id, { status: 'Accepted' });

        // Create activity for receiver
        const receiverAccount = accountService.getById(order.receiverId);
        if (receiverAccount) {
            activityService.add({
                id: `order-${order.id}-accepted`,
                audience: 'receiver',
                type: 'status_update',
                title: `Ordine #${order.id} accettato`,
                description: `Il mittente ha accettato il tuo ordine per la settimana ${order.targetWeek}/${order.targetYear}.`,
                timestamp: new Date(),
                isRead: false,
                actions: [
                    { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${order.id}` }
                ],
                metadata: {
                    orderId: order.id,
                    receiverId: order.receiverId,
                    shipperId: order.shipperId
                }
            });
        }

        setShowConfirmDialog(false);
        navigate('/shipper/orders');
    };

    const handleAcceptWithQuotes = async () => {
        if (!order) return;

        setShowConfirmDialog(false);
        setIsCreatingQuotes(true);

        try {
            // Accept the order first
            orderService.update(order.id, { status: 'Accepted' });

            // Create activity for receiver
            const receiverAccount = accountService.getById(order.receiverId);
            if (receiverAccount) {
                activityService.add({
                    id: `order-${order.id}-accepted`,
                    audience: 'receiver',
                    type: 'status_update',
                    title: `Ordine #${order.id} accettato`,
                    description: `Il mittente ha accettato il tuo ordine per la settimana ${order.targetWeek}/${order.targetYear}.`,
                    timestamp: new Date(),
                    isRead: false,
                    actions: [
                        { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${order.id}` }
                    ],
                    metadata: {
                        orderId: order.id,
                        receiverId: order.receiverId,
                        shipperId: order.shipperId
                    }
                });
            }

            // Get shipper ID
            const shipperId = storage.getItem('accountId') || order.shipperId;

            // Get all freight forwarders from address book
            const freightForwarders = addressBookService.search({
                accountId: shipperId,
                role: 'Freight Forwarder',
            });

            if (freightForwarders.length === 0) {
                // No freight forwarders available, just accept the order
                setIsCreatingQuotes(false);
                navigate('/shipper/orders');
                return;
            }

            // Calculate dates from week number
            const weekRange = getWeekRange(order.targetYear, order.targetWeek);
            const pickupDate = weekRange.start;
            const deliveryDate = new Date(weekRange.end.getTime() + 7 * 24 * 60 * 60 * 1000); // End of week + 7 days

            // Extract product information from order
            let product = '';
            let quantity: number | undefined;
            let unit = '';

            if (order.orderItems && order.orderItems.length > 0) {
                const firstItem = order.orderItems[0];
                product = firstItem.description;
                quantity = order.orderItems.reduce((sum, item) => sum + item.quantity, 0);
                unit = firstItem.unit;
            } else if (order.packingList.requested) {
                // Try to extract from packing list text
                product = order.packingList.requested.substring(0, 100);
            }

            // Build body text from order data
            const bodyParts: string[] = [];
            bodyParts.push(`Richiesta preventivo per ordine #${order.id}`);
            bodyParts.push('');

            if (order.packingList.requested) {
                bodyParts.push('Dettagli merce:');
                bodyParts.push(order.packingList.requested);
                bodyParts.push('');
            }

            if (order.orderItems && order.orderItems.length > 0) {
                bodyParts.push('Articoli ordine:');
                order.orderItems.forEach(item => {
                    bodyParts.push(`- ${item.description}: ${item.quantity} ${item.unit}`);
                    if (item.notes) {
                        bodyParts.push(`  Note: ${item.notes}`);
                    }
                });
                bodyParts.push('');
            }

            bodyParts.push(`Settimana target: ${order.targetWeek}/${order.targetYear}`);
            bodyParts.push(`Data ritiro prevista: ${pickupDate.toLocaleDateString('it-IT')}`);
            bodyParts.push(`Data consegna prevista: ${deliveryDate.toLocaleDateString('it-IT')}`);

            if (order.contractualInfo) {
                bodyParts.push('');
                bodyParts.push('Informazioni contrattuali:');
                if (order.contractualInfo.contractNumber) {
                    bodyParts.push(`Numero contratto: ${order.contractualInfo.contractNumber}`);
                }
                if (order.contractualInfo.terms) {
                    bodyParts.push(`Termini: ${order.contractualInfo.terms}`);
                }
            }

            if (order.notes) {
                bodyParts.push('');
                bodyParts.push('Note aggiuntive:');
                bodyParts.push(order.notes);
            }

            const body = bodyParts.join('\n');

            // Create extracted data
            const extractedData = {
                product: product || undefined,
                quantity: quantity,
                unit: unit || undefined,
                pickupDate: pickupDate,
                deliveryDate: deliveryDate,
                ...(order.contractualInfo?.terms && { specialRequirements: [order.contractualInfo.terms] }),
            };

            // Create quote request
            const newQuoteRequest = shipperQuoteService.create({
                subject: `Richiesta preventivo per ordine #${order.id}`,
                body: body,
                recipients: freightForwarders.map(ff => ff.id),
                extractedData: extractedData,
                shipperId: shipperId,
                orderId: order.id,
            });

            // Simulate some delay for better UX
            await new Promise(resolve => setTimeout(resolve, 1500));

            setIsCreatingQuotes(false);
            navigate(`/shipper/quotes/${newQuoteRequest.id}`);
        } catch (error) {
            console.error('Error creating quote request:', error);
            setIsCreatingQuotes(false);
            navigate('/shipper/orders');
        }
    };

    const handleReject = async () => {
        if (!order || !rejectionReason.trim()) return;

        orderService.update(order.id, {
            status: 'Rejected',
            rejectionReason: rejectionReason
        });

        // Create activity for receiver
        const receiverAccount = accountService.getById(order.receiverId);
        if (receiverAccount) {
            activityService.add({
                id: `order-${order.id}-rejected`,
                audience: 'receiver',
                type: 'status_update',
                title: `Ordine #${order.id} rifiutato`,
                description: `Il mittente ha rifiutato il tuo ordine. Motivo: ${rejectionReason}`,
                timestamp: new Date(),
                isRead: false,
                actions: [
                    { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${order.id}` }
                ],
                metadata: {
                    orderId: order.id,
                    receiverId: order.receiverId,
                    shipperId: order.shipperId
                }
            });
        }

        navigate('/shipper/orders');
    };

    const handleModify = async () => {
        if (!order || !proposedChanges.trim()) return;

        orderService.update(order.id, {
            status: 'Modified',
            proposedChanges: proposedChanges
        });

        // Create activity for receiver
        const receiverAccount = accountService.getById(order.receiverId);
        if (receiverAccount) {
            activityService.add({
                id: `order-${order.id}-modified`,
                audience: 'receiver',
                type: 'status_update',
                title: `Modifiche proposte per ordine #${order.id}`,
                description: `Il mittente ha proposto modifiche al tuo ordine.`,
                timestamp: new Date(),
                isRead: false,
                actions: [
                    { label: 'Vedi dettaglio ordine', href: `/receiver/orders/${order.id}` }
                ],
                metadata: {
                    orderId: order.id,
                    receiverId: order.receiverId,
                    shipperId: order.shipperId
                }
            });
        }

        navigate('/shipper/orders');
    };

    const handleSaveContractualInfo = () => {
        if (!order) return;

        orderService.update(order.id, {
            contractualInfo: {
                contractNumber: contractualInfo.contractNumber || undefined,
                contractDate: contractualInfo.contractDate ? new Date(contractualInfo.contractDate) : undefined,
                terms: contractualInfo.terms || undefined,
                notes: contractualInfo.notes || undefined
            }
        });

        // Reload order
        const updatedOrder = orderService.getById(id!);
        if (updatedOrder) setOrder(updatedOrder);
    };

    const handleSavePackingList = () => {
        if (!order) return;

        orderService.update(order.id, {
            packingList: {
                ...order.packingList,
                provided: packingListProvided,
                uploadedAt: new Date()
            }
        });

        // Reload order
        const updatedOrder = orderService.getById(id!);
        if (updatedOrder) setOrder(updatedOrder);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading')}</p>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <p className="text-lg text-tertiary mb-4">{t('detail.notFound')}</p>
                <Button onClick={() => navigate('/shipper/orders')} color="secondary">
                    {t('detail.backToList')}
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate('/shipper/orders')}
                        color="secondary"
                        size="sm"
                        iconLeading={ArrowLeft}
                    >
                        {t('detail.back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {t('detail.title')} #{order.id}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {t('detail.subtitle')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ShipperOnboardingHeaderAction />
                    <Badge color={getStatusColor(order.status)} size="lg">
                        {translateStatus(order.status)}
                    </Badge>
                </div>
            </div>

            {/* Order Info Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-brand-50 text-brand-600">
                            <User01 className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.receiver')}</p>
                            <p className="text-lg font-semibold text-primary">
                                {receiver?.legalName || order.receiverId}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-success-50 text-success-600">
                            <Calendar className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.targetWeek')}</p>
                            <p className="text-lg font-semibold text-primary">
                                Settimana {order.targetWeek}/{order.targetYear}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-warning-50 text-warning-600">
                            <Clock className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.createdAt')}</p>
                            <p className="text-lg font-semibold text-primary">
                                {order.createdAt.toLocaleDateString('it-IT')}
                            </p>
                        </div>
                    </div>
                </div>

                {order.respondedAt && (
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg p-3 bg-brand-50 text-brand-600">
                                <CheckCircle className="size-6" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.respondedAt')}</p>
                                <p className="text-lg font-semibold text-primary">
                                    {order.respondedAt.toLocaleDateString('it-IT')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Proposed Changes - Mostra quando lo Shipper ha richiesto modifiche */}
            {order.status === 'Modified' && order.proposedChanges && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary border-l-4 border-warning-600">
                    <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                        <AlertCircle className="size-5 text-warning-600" />
                        {t('detail.modificationRequested') || 'Richiesta di Modifica Inviata'}
                    </h2>
                    <div className="rounded-lg bg-warning-50 p-4 mb-4">
                        <p className="text-sm text-warning-700 whitespace-pre-wrap">{order.proposedChanges}</p>
                    </div>
                    <div className="text-sm text-tertiary">
                        {t('detail.modificationRequestedDescription') || 'Hai inviato una richiesta di modifica per questo ordine. Il Receiver riceverà una notifica e potrà accettare o rifiutare le modifiche proposte.'}
                    </div>
                </div>
            )}

            {/* Packing List Comparison */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.packingList.title')}</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm font-medium text-tertiary mb-2">
                            {t('detail.packingList.requested')}
                        </p>
                        <div className="rounded-lg bg-secondary p-4 min-h-[200px]">
                            <p className="text-sm text-primary whitespace-pre-wrap">
                                {order.packingList.requested || t('detail.packingList.notSpecified')}
                            </p>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-tertiary mb-2">
                            {t('detail.packingList.provided')}
                        </p>
                        <div className="space-y-3">
                            {order.packingList.provided ? (
                                <div className="rounded-lg bg-secondary p-4 min-h-[200px]">
                                    <p className="text-sm text-primary whitespace-pre-wrap">
                                        {order.packingList.provided}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <TextArea
                                        placeholder={t('detail.packingList.providePlaceholder')}
                                        value={packingListProvided}
                                        onChange={setPackingListProvided}
                                        rows={8}
                                    />
                                    <Button
                                        onClick={handleSavePackingList}
                                        color="primary"
                                        size="md"
                                        iconLeading={Upload01}
                                    >
                                        {t('detail.packingList.save')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contractual Info and Order Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contractual Info */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.contractualInfo.title')}</h2>
                    <div className="space-y-4">
                        <Input
                            label={t('detail.contractualInfo.contractNumber')}
                            value={contractualInfo.contractNumber}
                            onChange={(value) => setContractualInfo({ ...contractualInfo, contractNumber: value })}
                            size="md"
                            placeholder={t('detail.contractualInfo.contractNumberPlaceholder')}
                        />
                        <Input
                            label={t('detail.contractualInfo.contractDate')}
                            type="date"
                            value={contractualInfo.contractDate}
                            onChange={(value) => setContractualInfo({ ...contractualInfo, contractDate: value })}
                            size="md"
                        />
                        <TextArea
                            label={t('detail.contractualInfo.terms')}
                            value={contractualInfo.terms}
                            onChange={(value) => setContractualInfo({ ...contractualInfo, terms: value })}
                            rows={3}
                            placeholder={t('detail.contractualInfo.termsPlaceholder')}
                        />
                        <TextArea
                            label={t('detail.contractualInfo.notes')}
                            value={contractualInfo.notes}
                            onChange={(value) => setContractualInfo({ ...contractualInfo, notes: value })}
                            rows={3}
                            placeholder={t('detail.contractualInfo.notesPlaceholder')}
                        />
                        <Button
                            onClick={handleSaveContractualInfo}
                            color="primary"
                            size="md"
                        >
                            {t('detail.contractualInfo.save')}
                        </Button>
                    </div>
                </div>

                {/* Order Items */}
                {order.orderItems && order.orderItems.length > 0 && (
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.orderItems.title')}</h2>
                        <div className="space-y-2">
                            {order.orderItems.map((item) => (
                                <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                                    <div>
                                        <p className="text-sm font-medium text-primary">{item.description}</p>
                                        {item.notes && (
                                            <p className="text-xs text-tertiary mt-1">{item.notes}</p>
                                        )}
                                    </div>
                                    <div className="text-sm text-tertiary">
                                        {item.quantity} {item.unit}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Notes */}
            {order.notes && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.notes')}</h2>
                    <div className="rounded-lg bg-secondary p-4">
                        <p className="text-sm text-primary whitespace-pre-wrap">{order.notes}</p>
                    </div>
                </div>
            )}

            {/* Actions */}
            {order.status === 'Pending' && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.actions.title')}</h2>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={handleAccept}
                            color="primary"
                            size="md"
                            iconLeading={CheckCircle}
                        >
                            {t('detail.actions.accept')}
                        </Button>
                        <Button
                            onClick={() => setActionMode('reject')}
                            color="primary-destructive"
                            size="md"
                            iconLeading={XCircle}
                        >
                            {t('detail.actions.reject')}
                        </Button>
                        <Button
                            onClick={() => setActionMode('modify')}
                            color="secondary"
                            size="md"
                            iconLeading={Edit01}
                        >
                            {t('detail.actions.modify')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Rejection Modal */}
            <DialogTrigger isOpen={actionMode === 'reject'} onOpenChange={(open) => !open && setActionMode(null)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('detail.rejectModal.title')}
                                    </h2>
                                    <button
                                        onClick={() => setActionMode(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('buttons.cancel', { ns: 'common' })}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <TextArea
                                    label={t('detail.rejectModal.reason')}
                                    value={rejectionReason}
                                    onChange={setRejectionReason}
                                    rows={5}
                                    isRequired
                                    placeholder={t('detail.rejectModal.reasonPlaceholder')}
                                />
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => setActionMode(null)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('buttons.cancel', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        onClick={handleReject}
                                        color="primary-destructive"
                                        size="md"
                                        isDisabled={!rejectionReason.trim()}
                                    >
                                        {t('detail.rejectModal.submit')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Modify Modal */}
            <DialogTrigger isOpen={actionMode === 'modify'} onOpenChange={(open) => !open && setActionMode(null)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('detail.modifyModal.title')}
                                    </h2>
                                    <button
                                        onClick={() => setActionMode(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('buttons.cancel', { ns: 'common' })}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <TextArea
                                    label={t('detail.modifyModal.changes')}
                                    value={proposedChanges}
                                    onChange={setProposedChanges}
                                    rows={6}
                                    isRequired
                                    placeholder={t('detail.modifyModal.changesPlaceholder')}
                                />
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => setActionMode(null)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('buttons.cancel', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        onClick={handleModify}
                                        color="primary"
                                        size="md"
                                        isDisabled={!proposedChanges.trim()}
                                    >
                                        {t('detail.modifyModal.submit')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Confirm Create Quotes Dialog */}
            <DialogTrigger isOpen={showConfirmDialog} onOpenChange={(open) => !open && setShowConfirmDialog(false)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('orderAccept.confirmCreateQuotes.title', { ns: 'common' })}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('orderAccept.confirmCreateQuotes.message', { ns: 'common' })}
                                </p>
                                <div className="flex justify-between gap-3">
                                    <Button
                                        onClick={() => setShowConfirmDialog(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('buttons.cancel', { ns: 'common' })}
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={handleAcceptWithoutQuotes}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('orderAccept.confirmCreateQuotes.acceptOnly', { ns: 'common' })}
                                        </Button>
                                        <Button
                                            onClick={handleAcceptWithQuotes}
                                            color="primary"
                                            size="md"
                                        >
                                            {t('orderAccept.confirmCreateQuotes.createQuotes', { ns: 'common' })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Creating Quotes Loading Modal */}
            <ModalOverlay isOpen={isCreatingQuotes} onOpenChange={() => { }}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
                            <div className="flex flex-col items-center gap-4">
                                <LoadingIndicator type="line-spinner" size="lg" />
                                <p className="text-lg font-semibold text-primary">
                                    {t('orderAccept.creatingQuotes', { ns: 'common' })}
                                </p>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

